import { Perspective } from "../App";

interface PerspectiveObject {
    name: string;
    description: string;
    lottieJSONFileName: string;
}

enum PerspectivesName {
    General = "General",
    Mental = "Mental",
    Emotional = "Emotional",
    Physical = "Physical",
    Spiritual = "Spiritual"
}

const GENERAL_OBJ: PerspectiveObject = {
    name: PerspectivesName.General,
    description: "Each core capacity can be understood through physical, emotional, mental and spiritual perspectives. These four perspectives are represented by three aspects (mental, emotional, and physical) with the spiritual dimension experienced through “a sense of connection to all things, including the natural and manmade environments”. The spiritual dimension is considered to be pervasive and is therefore indivisible – it both embraces and imbues the three-folded nature of life (as expressed through the mental, emotional, and physical aspects).<br/><br/>The Learning for Well-being Framework suggests that mental, emotional, and physical aspects can be noticed in the way core capacities are experienced and expressed, and that together with the recognition of a spiritual dimension, they form a holistic, human-centred view on capacities.",
    lottieJSONFileName: ""
};

const MENTAL_OBJ: PerspectiveObject = {
    name: PerspectivesName.Mental,
    description: "A mental perspective refers to our cognitive and rational processes and the functions of envisioning, planning and valuing.<br/><br/>For example, a mental expression of listening can be associated with listening for information, facts, and thoughts.",
    lottieJSONFileName: "Anim-Perspectives-Mental"
};

const EMOTIONAL_OBJ: PerspectiveObject = {
    name: PerspectivesName.Emotional,
    description: "An emotional perspective refers both to our intrapersonal functions — our inner feelings, motivations— and our interpersonal functioning — our interactions with others.<br/>It includes the functions of feeling, relating, and connecting.<br/><br/>For example, an emotional expression of listening can be associated with opening and connecting with emotional content, felt intention, and bringing flow to communication.",
    lottieJSONFileName: "Anim-Perspectives-Emotional"
};

const PHYSICAL_OBJ: PerspectiveObject = {
    name: PerspectivesName.Physical,
    description: "The physical perspective refers to the physical senses, to our bodies, and to the material and natural environments.<br/><br/>It involves the functions of doing, detailing, and acting.<br/><br/>For example, listening from a physical perspective is associated with sensing the context and environment. It is about listening for the action needed in this moment.",
    lottieJSONFileName: "Anim-Perspectives-Physical"
};

const SPIRITUAL_OBJ: PerspectiveObject = {
    name: PerspectivesName.Spiritual,
    description: "The spiritual dimension can be experienced through a sense of connection to all things, including the natural and manmade environments. It relates to the simple state of being.<br/><br/>The spiritual dimension is pervasive and is therefore indivisible – it both embraces and imbues the three-folded nature of life (as expressed through the mental, emotional, and physical aspects).",
    lottieJSONFileName: "Anim-Perspectives-Spiritual"
};

export class PerspectiveContent {
    name: string;
    description: string;
    lottieJSONFileName: string;

    constructor(obj: PerspectiveObject) {
        this.name = obj.name;
        this.description = obj.description;
        this.lottieJSONFileName = obj.lottieJSONFileName;
    }
}

export class PerspectivesHelper {
    static readonly generalPerspective = new PerspectiveContent(GENERAL_OBJ)
    static readonly mentalPerspective = new PerspectiveContent(MENTAL_OBJ)
    static readonly emotionalPerspective = new PerspectiveContent(EMOTIONAL_OBJ)
    static readonly physicalPerspective = new PerspectiveContent(PHYSICAL_OBJ)
    static readonly spiritualPerspective = new PerspectiveContent(SPIRITUAL_OBJ)

    static perspectiveContentForPerspective(perspective: Perspective) {
        switch (perspective) {
            case Perspective.General:
                return this.generalPerspective;
            case Perspective.Mental:
                return this.mentalPerspective;
            case Perspective.Emotional:
                return this.emotionalPerspective;
            case Perspective.Physical:
                return this.physicalPerspective;
            case Perspective.Spiritual:
                return this.spiritualPerspective;
            default:
                alert("Capacity not found: " + perspective);
                return this.spiritualPerspective;
        }
    }
}